/**
 * IdleLockScreen — shown when the tab was locked for inactivity.
 *
 * Jul 2026: previously the idle lock redirected to `/?idle=1`, which
 * rendered the OLD React marketing landing (a different theme from the
 * static landing served at the edge) with authenticated widgets like
 * Herald floating on it — a jarring mismatch reported in production.
 * A locked-but-still-authenticated user should never see a marketing
 * page: they see this minimal lock screen and resume where they were.
 */
import React, { useCallback } from 'react';
import { useLanguage } from '@/contexts/LanguageContext';

export default function IdleLockScreen() {
  const { language } = useLanguage();
  const isAr = language === 'ar';

  const handleResume = useCallback(() => {
    let target = '/chat';
    try {
      const stashed = sessionStorage.getItem('bayenah_tab_lock_from');
      if (stashed && stashed.startsWith('/')) target = stashed;
      sessionStorage.removeItem('bayenah_tab_locked');
      sessionStorage.removeItem('bayenah_tab_lock_from');
    } catch (_e) { /* storage disabled — default target stands */ }
    // Full navigation (not SPA push) so AuthContext re-runs
    // supabase.auth.getSession() and rehydrates the still-valid
    // session from storage.
    window.location.assign(target);
  }, []);

  return (
    <div
      className="min-h-screen flex items-center justify-center px-6"
      style={{ background: '#FAFAF7' }}
      data-testid="idle-lock-screen"
      dir={isAr ? 'rtl' : 'ltr'}
    >
      <div className="text-center max-w-sm">
        <div
          className="mx-auto mb-6 flex items-center justify-center rounded-2xl"
          style={{ width: 56, height: 56, background: '#111111' }}
          aria-hidden
        >
          <img
            src="/brand/mark-white.png"
            alt=""
            style={{ width: 28, height: 28, objectFit: 'contain' }}
            draggable="false"
          />
        </div>
        <h1 className="text-[20px] font-medium mb-2" style={{ color: '#111111' }}>
          {isAr ? 'تم قفل الجلسة مؤقتاً' : 'Session paused'}
        </h1>
        <p className="text-[14px] leading-6 mb-6" style={{ color: '#6B6B6B' }}>
          {isAr
            ? 'أُقفلت هذه النافذة بعد فترة من عدم النشاط. جلستك لا تزال آمنة وصالحة — تابع من حيث توقفت.'
            : 'This tab was locked after a period of inactivity. Your session is still secure and valid — pick up right where you left off.'}
        </p>
        <button
          type="button"
          onClick={handleResume}
          className="px-6 py-2.5 rounded-lg text-[14px] font-medium"
          style={{ background: '#111111', color: '#FAFAF7', border: 'none', cursor: 'pointer' }}
          data-testid="idle-lock-resume"
        >
          {isAr ? 'استئناف العمل' : 'Resume'}
        </button>
      </div>
    </div>
  );
}
